import { Component } from '@theme/component';

/** Klaviyo metric the team-alert flow (Internal alert action) is triggered by. */
const KLAVIYO_EVENT = 'Pre-connect Submitted';

/** Klaviyo Client API: browser-safe, authenticated by the public key only. */
const KLAVIYO_EVENTS_URL = 'https://a.klaviyo.com/client/events/';
const KLAVIYO_REVISION = '2024-10-15';

/**
 * @typedef {object} PreConnectFields
 * @property {string} ranch_name
 * @property {string} website
 * @property {string} email
 */

/**
 * Pre-connect form (sections/connect-form.liquid, BUT-59): ranch name,
 * website or social page, email.
 *
 * Where the answers go, decided on each submit:
 * - `data-endpoint` set: POSTs the three fields as JSON to the backend.
 * - otherwise `data-klaviyo-key` set (the account's public API key): sends
 *   KLAVIYO_EVENT straight to Klaviyo's Client API with all three fields on
 *   the event and on the rancher's profile; the Klaviyo flow on that metric
 *   alerts the team. No Klaviyo app embed or onsite tracking is needed, and
 *   nothing is sent to the rancher.
 * - neither: does nothing, so the form submits natively as the store's
 *   contact form (also the no-JS path).
 *
 * The success message is shown only once the endpoint or Klaviyo has
 * accepted the send; a failure shows the error message and keeps the
 * answers in the form.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} ranchName - Ranch name input.
 * @property {HTMLInputElement} website - Website or social page input.
 * @property {HTMLInputElement} email - Email input.
 * @property {HTMLButtonElement} submit - Submit button.
 * @property {HTMLElement} success - Success message, hidden until a send succeeds.
 * @property {HTMLElement} error - Error message, hidden until a send fails.
 *
 * @extends {Component<Refs>}
 */
class ConnectFormComponent extends Component {
  requiredRefs = ['ranchName', 'website', 'email', 'submit', 'success', 'error'];

  /** @type {AbortController | null} */
  #request = null;

  /** @type {AbortController | null} */
  #listeners = null;

  connectedCallback() {
    super.connectedCallback();

    this.#listeners = new AbortController();
    // `submit` bubbles from the form to this element, so the listener survives
    // any re-render of the form markup inside.
    this.addEventListener('submit', this.#sendInsteadOfNativeSubmit, { signal: this.#listeners.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#request?.abort();
    this.#setBusy(false);
  }

  /** @returns {string} */
  get #endpoint() {
    return this.dataset.endpoint?.trim() ?? '';
  }

  /** @returns {string} */
  get #klaviyoKey() {
    return this.dataset.klaviyoKey?.trim() ?? '';
  }

  /** @returns {PreConnectFields} */
  get #fields() {
    const { ranchName, website, email } = this.refs;
    return {
      ranch_name: ranchName.value.trim(),
      website: website.value.trim(),
      email: email.value.trim(),
    };
  }

  /**
   * The browser has already validated the fields when `submit` fires.
   *
   * @param {SubmitEvent} event
   */
  #sendInsteadOfNativeSubmit = (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;

    let deliver = null;
    if (this.#endpoint) {
      deliver = this.#postToEndpoint;
    } else if (this.#klaviyoKey) {
      deliver = this.#sendToKlaviyo;
    }
    // No delivery available: let the form submit natively as a contact form.
    if (!deliver) return;

    event.preventDefault();
    this.#send(event.target, deliver);
  };

  /**
   * @param {HTMLFormElement} form
   * @param {(fields: PreConnectFields, signal: AbortSignal) => Promise<boolean>} deliver
   */
  async #send(form, deliver) {
    this.#request?.abort();
    const controller = new AbortController();
    this.#request = controller;

    this.#hideMessages();
    this.#setBusy(true);

    let sent = false;
    try {
      sent = await deliver(this.#fields, controller.signal);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error('connect-form-component: the request failed', error);
    }

    if (controller.signal.aborted) return;

    this.#request = null;
    this.#setBusy(false);

    if (sent) form.reset();
    this.#showMessage(sent ? this.refs.success : this.refs.error);
  }

  /**
   * @param {PreConnectFields} fields
   * @param {AbortSignal} signal
   * @returns {Promise<boolean>}
   */
  #postToEndpoint = async (fields, signal) => {
    const response = await fetch(this.#endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(fields),
      signal,
    });
    if (!response.ok) console.error(`connect-form-component: the endpoint answered ${response.status}`);
    return response.ok;
  };

  /**
   * The fields go on both the profile and the event, so the team alert can
   * read them either way ({{ person|lookup:'ranch_name' }} or
   * {{ event.ranch_name }}). Klaviyo answers 202 with an empty body.
   *
   * @param {PreConnectFields} fields
   * @param {AbortSignal} signal
   * @returns {Promise<boolean>}
   */
  #sendToKlaviyo = async (fields, signal) => {
    const url = `${KLAVIYO_EVENTS_URL}?company_id=${encodeURIComponent(this.#klaviyoKey)}`;
    const { email, ...profileProperties } = fields;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', revision: KLAVIYO_REVISION },
      body: JSON.stringify({
        data: {
          type: 'event',
          attributes: {
            properties: fields,
            metric: { data: { type: 'metric', attributes: { name: KLAVIYO_EVENT } } },
            profile: { data: { type: 'profile', attributes: { email, properties: profileProperties } } },
          },
        },
      }),
      signal,
    });
    if (!response.ok) console.error(`connect-form-component: Klaviyo answered ${response.status}`);
    return response.ok;
  };

  #hideMessages() {
    this.refs.success.hidden = true;
    this.refs.error.hidden = true;
  }

  /**
   * @param {HTMLElement} message
   */
  #showMessage(message) {
    message.hidden = false;
    // The message carries tabindex="-1"; focusing it reads it out.
    message.focus();
  }

  /**
   * @param {boolean} busy
   */
  #setBusy(busy) {
    this.refs.submit.disabled = busy;
    this.classList.toggle('_loading', busy);
    if (busy) {
      this.setAttribute('aria-busy', 'true');
    } else {
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('connect-form-component')) {
  customElements.define('connect-form-component', ConnectFormComponent);
}
